import React from 'react';
import { Avatar } from '@shoflo/polymer/components/Avatar/Avatar';
import { UncontrolledTooltip } from 'reactstrap';
import moment from 'moment-timezone';
import EventListItemMenu from './ListItemMenu';
import EventRowLabelCell from './EventRowLabelCell';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMapMarkerAlt } from '@fortawesome/free-solid-svg-icons';
import DefaultImage from 'components/DefaultImage';
import styled from 'styled-components';
import { getFormattedDateFromMillis } from 'utils/time';

const StyledEventPhoto = styled.img`
  max-height: 56px;
  max-width: 100px;
  cursor: pointer;
`;
const StyledTD = styled.td`
  border-top: none !important;
  border-bottom: 2px solid #f0f2f6;
  vertical-align: middle !important;
  padding: 1rem 0 !important;
`;
const EllipsedText = styled.div`
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  padding-right: 15px;
  min-height: 19px;
`;
const PhotoTD = styled(StyledTD)`
  position: relative;

  @media only screen and (max-width: 576px) {
    display: block;
    border: none;
    width: calc(100vw - 60px);
    padding: 0 !important;
    margin: 0 !important;

    img {
      margin-top: 15px;
    }
  }
`;
const TitleTD = styled(StyledTD)`
  @media only screen and (max-width: 576px) {
    display: block;
    width: calc(100vw - 60px);
  }
`;
const ShowEventLocationXS = styled.div`
  display: none;

  @media only screen and (max-width: 576px) {
    margin-top: 7px;
    display: block;
    white-space: normal;
    line-height: 20px;
  }
`;
const HideEventLocationXS = styled.em`
  display: inline-block;

  @media only screen and (max-width: 576px) {
    display: none;
  }
`;
const StyledEventTitle = styled(EllipsedText)`
  font-size: 18px;
  line-height: 30px;
  font-weight: 600;
  display: block;
  cursor: pointer;

  &:hover {
    color: #0095ff;
  }
`;
const StyledEventDescription = styled(EllipsedText)`
  font-size: 16px;
  line-height: 16px;
  color: #767676;
`;
const StyledEventSettings = styled(StyledTD)`
  text-align: right;

  .dropdown-menu.show {
    width: 200px;
    left: auto !important;
    right: 0;
    transform: translate3d(0px, 34px, 0px) !important;
  }
`;
const StyledTooltip = styled(UncontrolledTooltip)`
  .tooltip-inner {
    max-width: 400px;
  }
`;

export {
  StyledTooltip,
  StyledEventSettings,
  StyledEventDescription,
  StyledEventTitle,
  HideEventLocationXS,
  ShowEventLocationXS,
  PhotoTD,
  TitleTD,
  StyledTD,
  StyledEventPhoto,
};

const EventListItem = React.memo(
  ({
    event,
    hasMenu,
    onEditClick,
    onDuplicateClick,
    onViewClick,
    onArchiveClick,
    dateFormat,
  }) => {
    const onEditClickHandler = hasMenu && onEditClick.bind(this, event);
    const onViewClickHandler = onViewClick.bind(this, event);
    const onDuplicateClickHandler = onDuplicateClick.bind(this, event);
    const onArchiveClickHandler = onArchiveClick.bind(this, event);

    const imgNode = event.photo ? (
      <StyledEventPhoto
        src={event.photo}
        alt={`${event.title} logo`}
        onClick={onViewClickHandler}
      />
    ) : (
      <DefaultImage />
    );

    const eventStartDate = getFormattedDateFromMillis(
      event.startOn,
      dateFormat,
      event.timeZone,
    );
    const eventEndDate = getFormattedDateFromMillis(
      event.endOn,
      dateFormat,
      event.timeZone,
    );
    const eventDate = `${eventStartDate} - ${eventEndDate}`;
    const eventLocation = event.venue && `| ${event.venue}`;

    const isPastEvent = event.endOn < moment().subtract(7, 'days').valueOf();

    return (
      <tr data-testid={`event-${event.title}`}>
        <PhotoTD>{imgNode}</PhotoTD>
        <TitleTD>
          <StyledEventTitle
            onClick={onViewClickHandler}
            data-testid="eventName"
            className="fs-exclude"
          >
            {event.title}
          </StyledEventTitle>
          <StyledEventDescription data-testid="eventDescription">
            <span id={`event_${event._id}`}>
              {eventDate}{' '}
              <HideEventLocationXS>{eventLocation}</HideEventLocationXS>
            </span>
            {eventLocation && (
              <ShowEventLocationXS>
                {eventLocation.replace('| ', '')}{' '}
                <FontAwesomeIcon icon={faMapMarkerAlt} />
              </ShowEventLocationXS>
            )}
          </StyledEventDescription>
          {eventLocation && eventLocation.length >= 25 && (
            <StyledTooltip
              title={`event ${event._id} tooltip`}
              delay={300}
              placement="auto"
              target={`event_${event._id}`}
            >
              {`${eventDate} ${eventLocation}`}
            </StyledTooltip>
          )}
        </TitleTD>
        <StyledTD
          data-testid="eventLabels"
          key={`${event._id}-label-wrapper`}
          className="d-none d-lg-table-cell"
        >
          <EventRowLabelCell
            key={`${event._id}-label-cell`}
            labels={event.labels}
            max={2}
            eventId={event._id}
          />
        </StyledTD>
        <StyledTD className="d-none d-lg-table-cell">
          {event.owner && (
            <Avatar
              data-testid="eventOwner"
              key={event.owner._id}
              user={event.owner}
              className="fs-exclude"
            />
          )}
        </StyledTD>
        <StyledEventSettings
          data-testid="eventDropdown"
          className="d-none d-sm-table-cell"
        >
          {hasMenu && (
            <EventListItemMenu
              event={event}
              onArchive={onArchiveClickHandler}
              onDuplicate={onDuplicateClickHandler}
              onEdit={isPastEvent ? false : onEditClickHandler}
            />
          )}
        </StyledEventSettings>
      </tr>
    );
  },
);

EventListItem.displayName = 'EventListItem';

export default EventListItem;
